import { useQuery } from '@apollo/client';

import { GetSavannaCoursesForProgramDocument } from '@sandtech-hub-monorepo/ehub-client';

import { CurriculumError } from './CurriculumError';
import { InteractiveCourseAccordion } from './InteractiveCourseAccordion';

interface Props {
  programId: string;
  /** The learner's sandId. This tree only renders for someone enrolled. */
  sandId: string;
  /** Whether a live subscription currently unlocks this program's paid content. */
  covered: boolean;
  onSubscribe: () => void;
}

/**
 * Program structure for someone working through it.
 *
 * Every course opens, not just the first, because an enrolled learner needs
 * to reach all of them. Each fetches its own curriculum eagerly on first
 * render - see `InteractiveCourseAccordion`.
 *
 * Leaves are ACTIVITIES with links into Savanna, grouped by block. Whether a
 * paid one is openable still depends on coverage, so an enrolled learner
 * without a subscription gets the same tree with locks on the paid activities
 * and the unlock bar underneath, which is the upsell.
 *
 * It fetches its own course list rather than taking one, so a caller needs
 * only the ids it already has. Two places render it - the program detail page
 * and the enrolled program card on /learning - and Apollo serves the second
 * from cache when both ask about the same program.
 */
export const InteractiveStructure = ({
  programId,
  sandId,
  covered,
  onSubscribe,
}: Props) => {
  const { data, error } = useQuery(GetSavannaCoursesForProgramDocument, {
    variables: { programId },
    fetchPolicy: 'cache-first',
  });

  // Rendering nothing would read as "this program has no courses", which is
  // the one thing an enrolled learner knows to be false.
  if (error) {
    return <CurriculumError subject="this program's courses" />;
  }

  const locations = data?.savannaCoursesForProgram ?? [];

  return (
    <>
      {locations.map((location, index) => (
        <InteractiveCourseAccordion
          key={location.courseDefinitionId}
          location={location}
          index={index + 1}
          sandId={sandId}
          // The first course opens on arrival, so the page lands on content.
          defaultOpen={index === 0}
          paidUnlocked={covered}
          onSubscribe={onSubscribe}
        />
      ))}
    </>
  );
};
